import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { dateSelectors } from 'app-components/flux/date';
import { updateReportConfiguration } from 'app-components/flux/report-configurations/reportConfigurationsActions';
import { getReportConfigById } from 'app-components/flux/report-configurations/reportConfigurationsSelectors';
import type RootState from 'app-components/utils/redux-store/rootState';

import { getReportConfigFieldById } from '../reportConfigurationsSelectors';
import type { ReportConfigurationProps } from '../reportConfigurationsTypes';

interface UseReduxConfigOptions {
  shouldAdjustConfig?: boolean;
  shouldGenerateReport?: boolean;
  location?: string;
}

// TODO: use this in more places instead of the hook below
export const useReduxConfigField = <K extends keyof ReportConfigurationProps>(
  reportConfigIdProp: string | undefined,
  fieldName: K,
  options: UseReduxConfigOptions = {},
): [
  ReportConfigurationProps[K] | undefined,
  (value: ReportConfigurationProps[K], overrideOptions?: Partial<UseReduxConfigOptions>) => void,
] => {
  // For now, require reportConfigId - we'll update this to use context in a follow-up
  if (!reportConfigIdProp) {
    throw new Error('reportConfigId is required for useReduxConfigField');
  }
  const reportConfigId = reportConfigIdProp;
  const { shouldAdjustConfig = true, shouldGenerateReport = true, location } = options;
  const today = useSelector(dateSelectors.getCurrentDate);

  const reportConfigField = useSelector((state: RootState) =>
    getReportConfigFieldById(state, { id: reportConfigId, fieldName }),
  ) as ReportConfigurationProps[K];
  const dispatch = useDispatch();
  const reportConfig = useSelector((state: RootState) => getReportConfigById(state, { id: reportConfigId }));

  const updateReportConfigField = useCallback(
    (value: ReportConfigurationProps[K], overrideOptions?: Partial<UseReduxConfigOptions>) => {
      if (reportConfig) {
        const effectiveOptions = {
          shouldGenerateReport,
          shouldAdjustConfig,
          location: location ?? reportConfigId,
          ...overrideOptions,
        };

        dispatch(
          updateReportConfiguration(reportConfig.set(fieldName, value), {
            props: {
              generateReport: effectiveOptions.shouldGenerateReport,
              adjustReportConfiguration: effectiveOptions.shouldAdjustConfig,
              location: effectiveOptions.location,
              adjustProps: { changedField: fieldName, currentDate: today },
            },
          }),
        );
      }
    },
    [reportConfig, shouldGenerateReport, shouldAdjustConfig, location, reportConfigId, dispatch, fieldName, today],
  );
  return [reportConfigField, updateReportConfigField];
};

export const useReduxConfig = (reportConfigIdProp: string | undefined, options: UseReduxConfigOptions = {}) => {
  // For now, require reportConfigId - we'll update this to use context in a follow-up
  if (!reportConfigIdProp) {
    throw new Error('reportConfigId is required for useReduxConfig');
  }
  const reportConfigId = reportConfigIdProp;
  const { shouldAdjustConfig = true, shouldGenerateReport = true, location } = options;

  const reportConfig = useSelector((state: RootState) => getReportConfigById(state, { id: reportConfigId }));
  const dispatch = useDispatch();

  const updateReportConfig = useCallback(
    (fieldName: keyof ReportConfigurationProps, value: any, overrideOptions?: Partial<UseReduxConfigOptions>) => {
      if (!reportConfig) {
        // Report configuration not found
        return;
      }

      const effectiveOptions = {
        shouldGenerateReport,
        shouldAdjustConfig,
        location: location || reportConfigId,
        ...overrideOptions,
      };

      dispatch(
        updateReportConfiguration(reportConfig.set(fieldName as any, value), {
          props: {
            generateReport: effectiveOptions.shouldGenerateReport,
            adjustReportConfiguration: effectiveOptions.shouldAdjustConfig,
            location: effectiveOptions.location,
          },
        }),
      );
    },
    [reportConfig, dispatch, reportConfigId, shouldAdjustConfig, shouldGenerateReport, location],
  );

  return {
    reportConfig,
    updateReportConfig,
  };
};
